import { createTheme, ThemeOptions } from '@mui/material/styles';

// BaseQueue brand colors from Figma design
export const colors = {
  // Primary brand colors (aligned with prototype palette)
  primary: '#076FF7',
  primaryDark: '#065CC6',
  primaryLight: '#EFF6FF',

  // Text colors
  textPrimary: '#1F2937',
  textSecondary: '#6B7280',
  textTertiary: '#9CA3AF',
  textWhite: '#FFFFFF',

  // Background colors
  backgroundDefault: '#F4F6FB',
  backgroundPaper: '#FFFFFF',

  // Border colors
  border: '#E5E7EB',
  inputBorder: '#D1D5DB',

  // Status colors
  error: '#DC2626',
  success: '#16A34A',
  warning: '#F59E0B',

  // Gray scale
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
};

// Brand extras used by the console shell (sidebar, gradients, status tokens).
// Mirrors the prototype design tokens.
export const brand = {
  sidebarBg: '#0B1220',
  sidebarFg: '#CBD5E1',
  sidebarActive: '#1D2A44',
  gradBrand: 'linear-gradient(135deg, #076FF7 0%, #065CC6 100%)',
  // role avatar gradients
  gradAdmin: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
  gradProvider: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)',
  gradSupervisor: 'linear-gradient(135deg, #FBBF24 0%, #EA580C 100%)',
  gradDeactivated: 'linear-gradient(135deg, #CBD5E1 0%, #94A3B8 100%)',
  // queue/token status colors (see Open Question O1 — confirm enum)
  status: {
    completed: '#10B981',
    booked: '#3B82F6',
    waiting: '#60A5FA',
    in_progress: '#F59E0B',
    hold: '#F97316',
    skipped: '#FBBF24',
    cancelled: '#6B7280',
    no_show: '#EF4444',
  },
  // capacity indicator (customer-facing demand)
  capacity: { low: '#076FF7', medium: '#F59E0B', high: '#EF4444' },
};

// Custom theme configuration for BaseQueue application
const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: colors.textWhite,
    },
    secondary: {
      main: '#9c27b0',
      light: '#ba68c8',
      dark: '#7b1fa2',
      contrastText: colors.textWhite,
    },
    error: {
      main: colors.error,
      light: '#FEE2E2',
      dark: '#B91C1C',
    },
    warning: {
      main: colors.warning,
      light: '#FEF3C7',
      dark: '#D97706',
    },
    info: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
    },
    success: {
      main: colors.success,
      light: '#DCFCE7',
      dark: '#15803D',
    },
    background: {
      default: colors.backgroundDefault,
      paper: colors.backgroundPaper,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
      disabled: colors.textTertiary,
    },
    divider: colors.border,
  },
  typography: {
    fontFamily: [
      '"Inter"',
      '"DM Sans"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    // Based on Figma design - DM Sans font
    h1: {
      fontSize: '2.25rem', // 36px
      fontWeight: 700,
      lineHeight: '40px',
    },
    h2: {
      fontSize: '1.5rem', // 24px
      fontWeight: 600,
      lineHeight: '32px',
    },
    h3: {
      fontSize: '1.25rem', // 20px
      fontWeight: 600,
      lineHeight: '28px',
    },
    h4: {
      fontSize: '1.125rem', // 18px
      fontWeight: 600,
      lineHeight: '24px',
    },
    h5: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: '24px',
    },
    h6: {
      fontSize: '0.875rem', // 14px
      fontWeight: 600,
      lineHeight: '20px',
    },
    subtitle1: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: '24px',
    },
    subtitle2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      lineHeight: '20px',
    },
    body1: {
      fontSize: '1rem', // 16px
      fontWeight: 400,
      lineHeight: '24px',
    },
    body2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: '20px',
    },
    button: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: '24px',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem', // 12px
      fontWeight: 400,
      lineHeight: '16px',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: '16px',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 14, // Rounded corners from Figma (14px)
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          padding: '14px 24px',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
        },
        sizeLarge: {
          height: 56,
        },
      },
      defaultProps: {
        disableElevation: false,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${colors.border}`,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: colors.backgroundPaper,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary,
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: colors.inputBorder,
        },
        input: {
          padding: '14px 16px',
          fontSize: '1rem',
          fontWeight: 400,
          // NOTE: the OTP field sets its own larger/spaced style locally; this
          // global default must stay normal or it truncates selects (e.g. the
          // country-code dropdown) and over-spaces every input.
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: colors.gray700,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
};

// Create and export the theme
const theme = createTheme(themeOptions);

export default theme;
